import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';

import { doc, getDoc, updateDoc } from 'firebase/firestore';

import Swal from 'sweetalert2';

import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';
import { useToast } from '~/hooks/toast';
import IClient from '~/interfaces/IClient';
import IAddress from '~/interfaces/IAddress';

interface Params {
  id: string;
}

const ClientAddresses: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { addToast } = useToast();
  const [client, setClient] = useState<IClient>({} as IClient);
  const [addresses, setAddresses] = useState<IAddress[]>([]);
  const [search, setSearch] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const { id } = params;

  useEffect(() => {
    async function load() {
      const docSnap = await getDoc(doc(DB, 'clients', id));
      const item = docSnap.data() as IClient;

      setClient({
        ...item,
        uid: id,
      } as IClient);

      if (item.addresses) {
        const fmtAddresses = item.addresses.map(address => ({
          ...address,
          address_formatted: `${address.road}, ${address.number}, ${address.neighbourhood}, ${address.city} - ${address.state}`,
        }));

        setAddresses(fmtAddresses);
      }
    }

    if (id) {
      load();
    } else {
      history.goBack();
    }
  }, [id, history]);

  const filteredItems = useMemo(() => {
    const filtered = addresses.filter(item => {
      let result = true;

      if (item.road) {
        result = item.road
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase());
      }

      return result;
    });
    return filtered;
  }, [search, addresses]);

  const handleConfirmRemove = useCallback(
    async (uid: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirma a exclusão do endereço?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        const updateItems = [...client.addresses];

        const findIndex = updateItems.findIndex(find => find.uid === uid);

        if (findIndex !== -1) {
          updateItems.splice(findIndex, 1);

          await updateDoc(doc(DB, 'clients', id), {
            addresses: updateItems,
          });

          setClient(current => ({
            ...current,
            addresses: updateItems,
          }));

          setAddresses(updateItems);

          addToast({
            type: 'success',
            description: 'Endereço foi removido com sucesso.',
          });
        }
      }
    },
    [addToast, client, id],
  );

  return (
    <Dashboard containerMessage="col-xl-10 offset-xl-1">
      <div className="col-xl-10 offset-xl-1">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <div className="nk-block-head-sub">
                <Link className="back-to" to="/clients">
                  <em className="icon ni ni-arrow-left" />
                  <span>Voltar</span>
                </Link>
              </div>
              <h6 className="nk-block-title">
                Endereços de
                {client.name}
              </h6>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <em className="icon ni ni-search" />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          id="default-04"
                          placeholder="Buscar por endereço"
                          onChange={event => {
                            setSearch(event.target.value);
                          }}
                        />
                      </div>
                    </li>

                    <li className="nk-block-tools-opt">
                      <Link
                        to={`/clients/addresses/${id}/create`}
                        data-target="addProduct"
                        className="toggle btn btn-icon btn-primary d-md-none"
                      >
                        <em className="icon ni ni-plus" />
                      </Link>
                      <Link
                        to={`/clients/addresses/${id}/create`}
                        data-target="addProduct"
                        className="toggle btn btn-primary d-none d-md-inline-flex"
                      >
                        <em className="icon ni ni-plus" />
                        <span>Cadastrar</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="nk-block">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <div className="nk-tb-list">
                  <div className="nk-tb-item nk-tb-head">
                    <div className="nk-tb-col">
                      <span>Endereço</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Complemento</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Ponto de referência</span>
                    </div>
                    <div className="nk-tb-col nk-tb-col-tools">
                      <ul className="nk-tb-actions gx-1 my-n1">
                        <li className="mr-n1" />
                      </ul>
                    </div>
                  </div>

                  {filteredItems.map(item => (
                    <div className="nk-tb-item" key={item.uid}>
                      <div className="nk-tb-col">
                        <span className="title">{item.address_formatted}</span>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span className="fs-14px">{item.complement}</span>
                        </div>
                      </div>

                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span className="fs-14px">{item.reference}</span>
                        </div>
                      </div>

                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1">
                            <div className="dropdown">
                              <Link
                                to="#options"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                                data-toggle="dropdown"
                              >
                                <em className="icon ni ni-more-h" />
                              </Link>
                              <div className="dropdown-menu dropdown-menu-right">
                                <ul className="link-list-opt no-bdr">
                                  <li>
                                    <Link
                                      to={`/clients/addresses/${id}/edit/${item.uid}`}
                                    >
                                      <em className="icon ni ni-edit" />
                                      <span>Editar</span>
                                    </Link>
                                  </li>

                                  <li>
                                    <Link
                                      to="#remove"
                                      onClick={() => {
                                        handleConfirmRemove(item.uid);
                                      }}
                                    >
                                      <em className="icon ni ni-trash" />
                                      <span>Excluir</span>
                                    </Link>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default ClientAddresses;
